<script setup>
  import { ref, computed, watch } from "vue";
  import { storeToRefs } from "pinia";
  import { ArrowUp, Square } from "lucide-vue-next";
  import { VoiceInputButton } from "@/components/episto-ui/ai-assistant";
  import { useSurveyStore } from "@stores/survey/survey";
  import { useAiInsightConversationsStore } from "@stores/survey/assistant/aiInsightConversations";
  import { useAiInsightConversationMessagesStore } from "@stores/survey/assistant/aiInsightConversationMessages";

  const userQuestion = ref("");

  const { isLiveOrComplete } = storeToRefs(useSurveyStore());
  const aiInsightConversationsStore = useAiInsightConversationsStore();
  const { selectedConversation, isGeneratingMessage } = storeToRefs(aiInsightConversationsStore);
  const { generateConversationTitle, createConversation } = aiInsightConversationsStore;
  const aiInsightConversationMessagesStore = useAiInsightConversationMessagesStore();
  const { createConversationMessage, sendConversationMessage, cancelConversationMessage } =
    aiInsightConversationMessagesStore;
  const { conversationMessages } = storeToRefs(aiInsightConversationMessagesStore);

  const isNewConversation = computed(() => selectedConversation.value.id === null);
  const canSend = computed(
    () => Boolean(userQuestion.value.trim()) && isLiveOrComplete.value && !isGeneratingMessage.value,
  );

  const placeholder = computed(() =>
    isGeneratingMessage.value ? "Only one message can be generated at a time…" : "Ask a question…",
  );

  const stopGeneration = () => {
    if (!isGeneratingMessage.value) return;
    cancelConversationMessage(selectedConversation.value.id);
  };

  const sendMessage = async () => {
    if (!canSend.value) return;

    if (isNewConversation.value) await createConversation();

    // Collapse runs of blank lines so the bubble stays tidy.
    userQuestion.value = userQuestion.value.trim().replaceAll(/\n{2,}/g, "\n");

    createConversationMessage(userQuestion.value);
    userQuestion.value = "";

    await sendConversationMessage(selectedConversation.value.id);

    if (conversationMessages.value.length === 1) generateConversationTitle(selectedConversation.value.id);
  };

  watch(selectedConversation, () => {
    userQuestion.value = "";
  });
</script>

<template>
  <div class="relative mx-auto max-w-[760px] rounded-xl bg-muted/80">
    <textarea
      v-model="userQuestion"
      :placeholder="placeholder"
      :disabled="!isLiveOrComplete || isGeneratingMessage"
      rows="1"
      class="field-sizing-content max-h-[224px] w-full resize-none overflow-y-auto rounded-xl bg-transparent py-3 pl-4 pr-20 text-sm leading-[20px] placeholder:text-muted-foreground/60 focus:outline-none disabled:cursor-not-allowed"
      @keydown.enter.exact.prevent.stop="sendMessage" />

    <div class="absolute bottom-2 right-2 flex items-center gap-1">
      <VoiceInputButton
        v-model="userQuestion"
        :disabled="!isLiveOrComplete || isGeneratingMessage" />

      <!-- Stop the in-flight generation -->
      <button
        v-if="isGeneratingMessage"
        type="button"
        data-cy="assistant-stop-generation"
        class="inline-flex h-8 w-8 items-center justify-center rounded-full bg-foreground text-background transition-colors hover:bg-foreground/80"
        title="Stop"
        @click="stopGeneration">
        <Square class="h-3.5 w-3.5 fill-current" />
      </button>

      <!-- Send -->
      <button
        v-else
        type="button"
        data-cy="assistant-send-message"
        class="inline-flex h-8 w-8 items-center justify-center rounded-full transition-colors"
        :class="
          canSend
            ? 'bg-foreground text-background hover:bg-foreground/80'
            : 'cursor-not-allowed bg-muted-foreground/20 text-muted-foreground'
        "
        :disabled="!canSend"
        title="Send"
        @click="sendMessage">
        <ArrowUp
          class="h-4 w-4"
          stroke-width="2.5" />
      </button>
    </div>
  </div>
</template>
